import React from "react";
import Immutable from "immutable";
import {betterMemo} from "js/common/utils/more-memo";
import DependencyGraph from "js/admin/kpis/edit-kpis/tabs/relationships/dependency-graph";
import CycleGraph from "js/admin/kpis/edit-kpis/tabs/relationships/cycle-graph";
import ErrorMsg from "js/common/views/error";
import Icon from "js/admin/common/icon";
import Hint from "js/admin/common/hint";
import { CustomThemeContext } from "js/common/themes/CustomThemeProvider";

const MetricRelationships = betterMemo(
    "MetricRelationships",
    ({kpiIdToDependentKpis, kpiIdToContributingKpis, kpi, onDependencyClick, cyclingKpis, rootNodes}) => {

      const { theme } = React.useContext(CustomThemeContext);

      const idToNode = !cyclingKpis && getIdToNode(
          kpi.get("id"),
          rootNodes,
          kpiIdToDependentKpis);

      return <div style={{width: "100%"}}>
        {cyclingKpis ? <>
          <ErrorMsg style={{margin: "0 5px"}} text="Unable to load graph as config contains a cycle." />
          <CycleGraph
              currentKpiId={kpi.get("id")}
              onClick={onDependencyClick}
              cyclingKpis={cyclingKpis}
          />
        </> : <>
          <Hint style={{margin: "1rem"}}>
            <Icon icon="info" style={{color: theme.palette.hints.text}} />
            <span>Click on a metric in the tree to jump to that metric in the editor.  You can hover over a dependency icon on a metric to highlight the relevant connections.</span>
          </Hint>
          <DependencyGraph
              currentKpiId={kpi.get("id")}
              idToNode={idToNode}
              kpiIdToContributingKpis={kpiIdToContributingKpis}
              kpiIdToDependentKpis={kpiIdToDependentKpis}
              onDependencyClick={onDependencyClick}
          />
        </>}
      </div>;
    });

const getIdToNode = (kpiId, rootNodes, kpiIdToDependentKpis) => {
  let idToNode = Immutable.Map();
  rootNodes.forEach(node => {
    idToNode = idToNode.set(node.get("id"), node);
    let visitedNodeIdToDependencyTypes = Immutable.Map();
    idToNode = getAllDependentNodes(node.get("id"), idToNode, kpiIdToDependentKpis, visitedNodeIdToDependencyTypes);
  });
  return idToNode;
};

const getAllDependentNodes = (nodeId, idToNode, kpiIdToDependentKpis, visitedNodeIdToDependencyTypes) => {

  const dependentKpis = kpiIdToDependentKpis.get(nodeId, Immutable.List());
  dependentKpis.forEach(dk => {
    if (visitedNodeIdToDependencyTypes.get(dk.get("id")) === dk.get("dependencyType")) {
      return;
    } else {
      idToNode = idToNode.updateIn(
          [nodeId, "edgesOut"],
          Immutable.Map(),
          map => {
            if (map.get(dk.get("id"))) {
              return map.updateIn(
                  [dk.get("id"), "dependencyTypes"],
                  Immutable.Set(),
                  types => types.add(dk.get("dependencyType")));
            } else {
              return map.set(
                  dk.get("id"),
                  Immutable.fromJS({nodeId: dk.get("id"), dependencyTypes: Immutable.Set([dk.get("dependencyType")])}));
            }
          });
      if (idToNode.get(dk.get("id"))) {
        idToNode = idToNode
            .updateIn([dk.get("id"), "dependencyTypeToParents", dk.get("dependencyType")], Immutable.Set(), parents => parents.add(nodeId));
      } else {
        idToNode = idToNode.set(dk.get("id"), dk
            .set("dependencyTypeToParents", Immutable.Map({[dk.get("dependencyType")]: Immutable.Set([nodeId])}))
            .delete("dependencyType")
        );
        idToNode = getAllDependentNodes(
            dk.get("id"),
            idToNode,
            kpiIdToDependentKpis,
            visitedNodeIdToDependencyTypes.set(dk.get("id"), dk.get("dependencyType")));
      }
      if(dk.get("dependencyType") === "COMBINATION") {
        idToNode = idToNode.setIn([dk.get("id"), "combineType"], dk.get("combineType"))
      }
    }
  });
  return idToNode;
};

export default MetricRelationships;
